import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db, auth } from "../firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import "./AdminModerationPage.css";
import { deletePostWithSubcollections } from "../utils/deletePostWithSubcollections";
import { deleteNewsArticleWithSubcollections } from "../utils/deleteNewsArticleWithSubcollections";
import CommentSection from "./CommentSection";

const AdminModerationPage = () => {
  const [posts, setPosts] = useState([]);
  const [newsArticles, setNewsArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [moderationBusy, setModerationBusy] = useState("");


  const navigate = useNavigate(); // ✅ Must be at top, before any return


  // ✅ Verify Admin Access
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setIsAdmin(false);
        return;
      }
      try {
        const tokenResult = await user.getIdTokenResult();
        setIsAdmin(tokenResult.claims?.admin === true);
      } catch (err) {
        console.error("Failed to verify admin claim:", err);
        setIsAdmin(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // ✅ Fetch Hidden or Removed Posts
  useEffect(() => {
    if (!isAdmin) return;
    const q = query(
      collection(db, "posts"),
      where("isHidden", "==", true),
      orderBy("hiddenAt", "desc")
    );

   const unsub = onSnapshot(
  q,
  (snapshot) => {
    const list = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data(), itemType: 'post' }));
    setPosts(list);
    setLoading(false); // ✅ Always stop loading, even if list is empty
  },
  (error) => {
    console.error("Error fetching moderation posts:", error);
    setLoading(false); // ✅ Also stop loading on error
  }
);

    return () => unsub();
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return undefined;

    const q = query(
      collection(db, "newsArticles"),
      where("isHidden", "==", true),
      orderBy("hiddenAt", "desc")
    );

    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const list = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          itemType: 'news',
        }));
        setNewsArticles(list);
      },
      (error) => {
        console.error("Error fetching moderation news articles:", error);
      }
    );

    return () => unsub();
  }, [isAdmin]);

  if (!isAdmin)
    return (
      <div style={{ padding: "20px" }}>
        <h2>Access Denied</h2>
        <p>This page is for administrators only.</p>
      </div>
    );

  if (loading)
    return (
      <div style={{ padding: "20px" }}>
        <h3>Loading hidden posts...</h3>
      </div>
    );

  const handleRestore = async (post) => {
    const busyKey = `post:${post.id}:restore`;
    if (moderationBusy) return;
    if (post.authorDeleted === true) {
      alert("Cannot restore a post deleted by the author.");
      return;
    }
    if (!window.confirm("Restore this post to public view?")) return;
    const postRef = doc(db, "posts", post.id);
    setModerationBusy(busyKey);
    try {
      await updateDoc(postRef, {
        isHidden: false,
        hiddenReason: "",
        hiddenBy: "",
        hiddenAt: null,
        restored: true,
        restoredOn: new Date(),
      });
      alert("✅ Post restored.");
    } finally {
      setModerationBusy("");
    }
  };

  const handleDelete = async (postId) => {
    const busyKey = `post:${postId}:delete`;
    if (moderationBusy) return;
    if (!window.confirm("Permanently delete this post?")) return;
    setModerationBusy(busyKey);
    try {
      await deletePostWithSubcollections(postId);
      alert("🗑️ Post permanently deleted.");
    } finally {
      setModerationBusy("");
    }
  };

  const handleRestoreNews = async (articleId) => {
    const busyKey = `news:${articleId}:restore`;
    if (moderationBusy) return;
    if (!window.confirm("Restore this article to public view?")) return;
    const articleRef = doc(db, "newsArticles", articleId);
    setModerationBusy(busyKey);
    try {
      await updateDoc(articleRef, {
        isHidden: false,
        hiddenReason: "",
        hiddenBy: "",
        hiddenAt: null,
        flaggedForReview: false,
      });
      alert("✅ Article restored.");
    } finally {
      setModerationBusy("");
    }
  };

  const handleDeleteNews = async (articleId) => {
    const busyKey = `news:${articleId}:delete`;
    if (moderationBusy) return;
    if (!window.confirm("Permanently delete this article?")) return;
    setModerationBusy(busyKey);
    try {
      await deleteNewsArticleWithSubcollections(articleId);
      alert("🗑️ Article permanently deleted.");
    } finally {
      setModerationBusy("");
    }
  };

  


return (
  <div className="admin-moderation">
    <div className="moderation-header">
      <h1>🛠️ Moderation Dashboard</h1>
      <button className="back-button" onClick={() => navigate("/thewire")}>
        ← Back to The Wire
      </button>
    </div>


      <p>
        Showing {posts.length} hidden/removed posts.
        Click “View Details” for reasons and reporters.
      </p>

      {posts.length === 0 ? (
        <p>No posts currently hidden.</p>
      ) : (
        posts.map((post) => (
          <div
            key={post.id}
            className={`moderation-card ${
              post.hiddenBy === "system" ? "auto-hidden" : "admin-hidden"
            }`}
          >
            <h3>{post.text?.slice(0, 120) || "Untitled post"}</h3>
            <p>
              <strong>Topic:</strong> {post.topic || "N/A"} <br />
              <strong>Author:</strong>{" "}
              {post.privacyTier === "anonymous"
                ? "🎩 Anonymous"
                : post.userName || "Unknown"}{" "}
              — {post.agency || ""}
            </p>

            <p>
              <strong>Reason:</strong>{" "}
              {post.hiddenReason || "No reason recorded."}
            </p>
            <div className="moderation-badges">
              {post.authorDeleted === true ? (
                <span>Deleted by author</span>
              ) : null}
              {post.containsModeratedContent === true ? (
                <span>Contains moderated comment/reply</span>
              ) : null}
            </div>

            <button className="view-details" onClick={() => setSelectedPost(post)}>
              View Details
            </button>
            {post.authorDeleted === true ? null : (
              <button
                className="restore"
                disabled={Boolean(moderationBusy)}
                onClick={() => handleRestore(post)}
              >
                {moderationBusy === `post:${post.id}:restore` ? "Restoring..." : "Restore"}
              </button>
            )}
            <button
              className="delete"
              disabled={Boolean(moderationBusy)}
              onClick={() => handleDelete(post.id)}
            >
              {moderationBusy === `post:${post.id}:delete` ? "Deleting..." : "Delete Permanently"}
            </button>
          </div>
        ))
      )}

      <div style={{ marginTop: "28px" }}>
        <p>
          Showing {newsArticles.length} hidden/removed news articles.
          Click “View Details” for reasons and reporters.
        </p>

        {newsArticles.length === 0 ? (
          <p>No news articles currently hidden.</p>
        ) : (
          newsArticles.map((article) => (
            <div
              key={article.id}
              className={`moderation-card ${
                article.hiddenBy === "system" ? "auto-hidden" : "admin-hidden"
              }`}
            >
              <h3>{article.title?.slice(0, 120) || "Untitled article"}</h3>
              <p>
                <strong>Source:</strong> {article.source || "N/A"} <br />
                <strong>Published:</strong>{" "}
                {article.publishedAt?.toDate?.().toLocaleString?.() || "N/A"}
              </p>

              <p>
                <strong>Reason:</strong>{" "}
                {article.hiddenReason || "No reason recorded."}
              </p>

              <button className="view-details" onClick={() => setSelectedPost(article)}>
                View Details
              </button>
              <button
                className="restore"
                disabled={Boolean(moderationBusy)}
                onClick={() => handleRestoreNews(article.id)}
              >
                {moderationBusy === `news:${article.id}:restore` ? "Restoring..." : "Restore"}
              </button>
              <button
                className="delete"
                disabled={Boolean(moderationBusy)}
                onClick={() => handleDeleteNews(article.id)}
              >
                {moderationBusy === `news:${article.id}:delete` ? "Deleting..." : "Delete Permanently"}
              </button>
            </div>
          ))
        )}
      </div>

      {selectedPost && (
        <div className="modal-overlay" onClick={() => setSelectedPost(null)}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Post Details</h3>
            <p>{selectedPost.title || selectedPost.text}</p>
            {selectedPost.summary ? <p>{selectedPost.summary}</p> : null}
            {selectedPost.url ? (
              <p>
                <strong>Source URL:</strong>{" "}
                <a href={selectedPost.url} target="_blank" rel="noopener noreferrer">
                  {selectedPost.url}
                </a>
              </p>
            ) : null}
            <p>
              <strong>Hidden By:</strong> {selectedPost.hiddenBy}
            </p>
            <p>
              <strong>Flag Count:</strong> {selectedPost.reportCount || 0}
            </p>

            {selectedPost.reporterDetails?.length > 0 ? (
              <ul>
                {selectedPost.reporterDetails.map((r, i) => (
                  <li key={i}>
                    {r.username} — {r.agency}: <em>{r.reason}</em>
                  </li>
                ))}
              </ul>
            ) : (
              <p>No reporters listed.</p>
            )}

            {selectedPost.itemType === "post" ? (
              <div className="moderation-comments-panel">
                <h4>Comments and Replies</h4>
                <CommentSection
                  postId={selectedPost.id}
                  post={selectedPost}
                  showInput={false}
                  initiallyExpanded
                  isAdmin
                />
              </div>
            ) : null}

            <button onClick={() => setSelectedPost(null)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminModerationPage;
